import {
  approvalActionPreviewSchema,
  approvalActionResponseSchema,
  approvalLookupsSchema,
  approvalWorkflowListResponseSchema,
  approvalWorkflowSchema,
  okResponseSchema,
  ticketApprovalSchema,
  type DecideApprovalRequest,
  type UpsertApprovalStepRequest,
  type UpsertApprovalWorkflowRequest,
} from '@gb-ticket/shared-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api';

export const approvalLookupsQueryKey = ['approvals', 'lookups'] as const;
export const approvalWorkflowsQueryKey = ['approvals', 'workflows'] as const;

export function useApprovalLookups() {
  return useQuery({
    queryKey: approvalLookupsQueryKey,
    queryFn: () => apiFetch('/approvals/lookups', approvalLookupsSchema),
  });
}

export function useApprovalWorkflows() {
  return useQuery({
    queryKey: approvalWorkflowsQueryKey,
    queryFn: () => apiFetch('/approvals/workflows', approvalWorkflowListResponseSchema),
  });
}

export function useCreateApprovalWorkflow() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: UpsertApprovalWorkflowRequest) =>
      apiFetch('/approvals/workflows', approvalWorkflowSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: approvalWorkflowsQueryKey });
    },
  });
}

export function useUpdateApprovalWorkflow() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpsertApprovalWorkflowRequest }) =>
      apiFetch(`/approvals/workflows/${id}`, approvalWorkflowSchema, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: approvalWorkflowsQueryKey });
    },
  });
}

export function useDeleteApprovalWorkflow() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/approvals/workflows/${id}`, okResponseSchema, { method: 'DELETE' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: approvalWorkflowsQueryKey });
    },
  });
}

export function useCreateApprovalStep() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ workflowId, body }: { workflowId: string; body: UpsertApprovalStepRequest }) =>
      apiFetch(`/approvals/workflows/${workflowId}/steps`, approvalWorkflowSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: approvalWorkflowsQueryKey });
    },
  });
}

export function useUpdateApprovalStep() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpsertApprovalStepRequest }) =>
      apiFetch(`/approvals/steps/${id}`, approvalWorkflowSchema, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: approvalWorkflowsQueryKey });
    },
  });
}

export function useDeleteApprovalStep() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/approvals/steps/${id}`, approvalWorkflowSchema, { method: 'DELETE' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: approvalWorkflowsQueryKey });
    },
  });
}

export function useDecideApproval(ticketRef: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ requestId, body }: { requestId: string; body: DecideApprovalRequest }) =>
      apiFetch(`/approvals/requests/${requestId}/decide`, ticketApprovalSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['tickets'] });
      await queryClient.invalidateQueries({ queryKey: ['tickets', ticketRef] });
    },
  });
}

export function useApprovalActionPreview(token: string | undefined) {
  return useQuery({
    queryKey: ['approvals', 'actions', token],
    queryFn: () => apiFetch(`/approvals/actions/${token}`, approvalActionPreviewSchema),
    enabled: Boolean(token),
  });
}

export function useSubmitApprovalAction(token: string) {
  return useMutation({
    mutationFn: (body: DecideApprovalRequest) =>
      apiFetch(`/approvals/actions/${token}`, approvalActionResponseSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
  });
}
